import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { JWTToken } from '@/types/branded';
import type { Email } from '@/types/branded';
import { createEmail, createJWTToken, createUserId } from '@/types/branded';
import type { Role, User } from '@/types/user';

import { tokenManager } from '../api/token-manager';
import { decodeToken, isTokenValid } from './utils';

type AuthState = {
  user: User | null;
  token: JWTToken | null;
  isAuthenticated: boolean;
  isLoading: boolean;
};

type AuthActions = {
  login: (token: string, rememberMe?: boolean) => void;
  logout: () => void;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: Role[]) => boolean;
  initializeAuth: () => void;
  setLoading: (loading: boolean) => void;
};

type AuthStore = AuthState & AuthActions;

function extractUserFromToken(token: JWTToken): User | null {
  const payload = decodeToken(token);
  if (!payload) return null;

  return {
    id: createUserId(payload.sub),
    email: createEmail(payload.email) as Email,
    username: payload.username,
    roles: payload.roles || ['client'],
    firstName: payload.firstName,
    lastName: payload.lastName,
    phone: payload.phone,
    position: payload.position,
  };
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,

      login: (tokenString: string, rememberMe: boolean = true) => {
        const token = createJWTToken(tokenString);

        if (!isTokenValid(token)) {
          get().logout();
          return;
        }

        const user = extractUserFromToken(token);
        if (!user) {
          get().logout();
          return;
        }

        tokenManager.setToken(token, rememberMe);

        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        tokenManager.clearToken();

        set({
          token: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      hasRole: (role: Role): boolean => {
        const { user } = get();
        return user?.roles.includes(role) ?? false;
      },

      hasAnyRole: (roles: Role[]): boolean => {
        const { user } = get();
        if (!user) return false;
        return roles.some((role) => user.roles.includes(role));
      },

      initializeAuth: () => {
        const storedToken = tokenManager.getToken();

        if (!storedToken || !isTokenValid(storedToken)) {
          tokenManager.clearToken();
          set({
            token: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return;
        }

        const user = extractUserFromToken(storedToken);
        if (!user) {
          tokenManager.clearToken();
          set({
            token: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return;
        }

        set({
          token: storedToken,
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.initializeAuth();
        }
      },
    }
  )
);
